/**
 * Filters dayparts based on user permissions and other conditions
 * @param {boolean} isSmolt - Indicates if the user is a smolt
 * @param {boolean} isOptoScaleAdmin - Indicates if the user is an OptoScale admin
 * @param {boolean} hasLivingWeightAllTagsEnabled - Indicates if the user has all tags enabled for living weight
 * @returns {string[]} - An array of daypart tags
 * Function to get disabled day tags based on the selected daypart tag
 * @param {boolean} isSmolt - Indicates if the user is a smolt
 * @param {string} daypartTag - The selected daypart tag
 * @returns {string[]} - An array of disabled day tags
 */

import {
    ALL_DAYPART_TAGS,
    DEFAULT_DAYPART_TAG,
    type OptoDayPartTagType,
    SMOLT_DAYPART_TAG,
} from '@/contexts/cage-filter-context';

/**
 * The daypart a cage's weight data is read and shown for.
 *
 * Smolt tanks have no day/night split, so they always use `all`, whatever the page filter says.
 * Every overview request and every card has to resolve it this way, or a smolt pen's cards (read
 * from the requested daypart's estimate) disagree with its charts and with its smolt report, which
 * both use `all`.
 */
export function resolveWeightDaypartTag(
    isSmolt: boolean,
    daypartTag: OptoDayPartTagType | undefined
): OptoDayPartTagType {
    return isSmolt ? SMOLT_DAYPART_TAG : (daypartTag ?? DEFAULT_DAYPART_TAG);
}

export default function filterGraphDayparts(
    isSmolt: boolean,
    isOptoScaleAdmin: boolean,
    hasLivingWeightAllTagsEnabled: boolean
): string[] {
    if (isSmolt) {
        return [SMOLT_DAYPART_TAG];
    }

    if (isOptoScaleAdmin || hasLivingWeightAllTagsEnabled) {
        return ALL_DAYPART_TAGS;
    }

    return [DEFAULT_DAYPART_TAG];
}
